import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useBunja } from 'bunja/react';
import { useSetAtom } from 'jotai';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import { putLuckyDrawTokensApi } from '@flipster/apis/v1/campaign';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Modal } from '@flipster/react/design-system/modal';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { Explain } from '@flipster/react/design-utils/explain';
import Formatted from '@flipster/react/design-utils/formatted';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import { css } from '@flipster/styled-system/css';
import { center, flex, hstack, stack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { ServerError } from '@flipster/utils/constants/server-error';

import { ImgOrLottie } from '../assets';
import { luckyDrawBunja } from './state';

interface Props {
  fromTokenIcon: { title?: string | null; url?: string | null };
  fromTokens: number;
  toTokenIcon: { title?: string | null; url?: string | null };
  exchangeCost: { from: string; to: string; cost: number };
  isOpen: boolean;
  onClose: () => void;
  onExit: () => void;
}

export const ConvertModal = ({
  fromTokenIcon,
  fromTokens,
  toTokenIcon,
  exchangeCost,
  isOpen,
  onClose,
  onExit,
}: Props) => {
  const router = useRouter();
  const t = useTranslations();

  const [fromAmount, setFromAmount] = useState<number | undefined>(undefined);
  const [toAmount, setToAmount] = useState<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);

  const { invalidLuckyDraws, setNewDrawResultAtom } = useBunja(luckyDrawBunja);
  const setNewDrawResult = useSetAtom(setNewDrawResultAtom);

  const cost = exchangeCost?.cost ?? 0;
  const disabled = !fromAmount || !toAmount || !!error;

  const handleSetToAmount = (value: string) => {
    const numberValue = Number(value);
    setError(null);
    setToAmount(numberValue);
    const newFromAmount = numberValue * cost;
    setFromAmount(newFromAmount);
    if (newFromAmount > fromTokens) {
      setError(t('Campaign.r251124.You_don_t_have_enough_token', { token: fromTokenIcon?.title }));
    }
  };

  const handleMaxAmount = () => {
    setError(null);
    const newFromAmount = Math.max(0, Math.floor((fromTokens ?? 0) / cost));
    setFromAmount(newFromAmount * cost);
    setToAmount(newFromAmount);
  };

  const exchangeMutation = useMutation({
    mutationFn: putLuckyDrawTokensApi,
    onSuccess(response) {
      Object.entries(response.data).forEach(([key, value]) => {
        setNewDrawResult({ slug: key, token: value });
      });
      snackbar.success({
        message: (
          <FormattedMessage id='Campaign.r251124.Your_token_exchanged' values={{ token: fromTokenIcon?.title }} />
        ),
      });
    },
    onError(error) {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 401:
          snackbar.error({ message: <FormattedMessage id='Common.r250918.Please_log_in' /> });
          router.push('/signup');
          break;
        case 403:
          snackbar.error({
            message: (
              <FormattedMessage
                id='Campaign.r251124.Your_token_used_refresh_again_multiline'
                values={{ token: fromTokenIcon?.title }}
              />
            ),
          });
          invalidLuckyDraws();
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
    },
    onSettled() {
      onClose();
    },
  });

  return (
    <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
      <Modal.Header>
        <Modal.Title label={<FormattedMessage id='Common.r251124.Exchange' />} />
      </Modal.Header>
      <Modal.Content css={{ textStyle: 'Text/L/Regular', color: 'content.default.level-3' }}>
        <div
          className={stack({
            gap: 12,
            py: 16,
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderColor: 'border.default.level-1',
            mb: 32,
          })}
        >
          <p>
            <FormattedMessage id='Campaign.r251124.Available_token' values={{ token: fromTokenIcon?.title }} />
          </p>
          <Formatted.Number
            value={fromTokens}
            css={{ textStyle: 'Title/M/Monospace', color: 'content.default.level-5' }}
          />
        </div>
        <div className={hstack({ justify: 'space-between', mb: 8 })}>
          <span>
            <FormattedMessage id='Common.r251008.From' />
          </span>
          <span>
            <FormattedMessage id='Common.r251008.To' />
          </span>
        </div>
        <div className={hstack({ justify: 'space-around', gap: 12 })}>
          <div className={center({ flex: 1, p: 8 })}>
            <ImgOrLottie
              url={fromTokenIcon.url}
              alt='token icon'
              css={{ w: '100%', h: '100%', aspectRatio: '1 / 1', maxW: 100 }}
            />
          </div>
          <ArrowRightIcon size={20} color={token('colors.content.default.level-2')} />
          <div className={center({ flex: 1, p: 8 })}>
            <ImgOrLottie
              url={toTokenIcon.url}
              alt='token icon'
              css={{ w: '100%', h: '100%', aspectRatio: '1 / 1', maxW: 100 }}
            />
          </div>
        </div>
        <p className={css({ py: 12 })}>Amount</p>
        <div className={hstack({ justify: 'space-around', gap: 12 })}>
          <FormattedNumberInput
            value={fromAmount != null ? fromAmount.toString() : undefined}
            className={inputStyle}
            disabled
          />
          <ArrowRightIcon size={20} color={token('colors.content.default.level-2')} />
          <FormattedNumberInput
            value={toAmount != null ? toAmount.toString() : undefined}
            onChange={handleSetToAmount}
            placeholder={t('Campaign.r251124.Set_amount')}
            inputMode='numeric'
            className={inputStyle}
          />
        </div>
        <div className={hstack({ gap: 6, mt: 12, mb: 24 })}>
          {[1, 2, 5, 10].map((item) => {
            return (
              <button
                type='button'
                key={`plus-${item}`}
                className={buttonStyle}
                onClick={() => handleSetToAmount(Math.floor((toAmount ?? 0) + item).toString())}
              >
                +{item}
              </button>
            );
          })}
          <button type='button' className={buttonStyle} onClick={handleMaxAmount}>
            <FormattedMessage id='Common.r250401.Max' />
          </button>
        </div>
        <Explain.Error css={flex.raw({ justify: 'center' })}>{error}</Explain.Error>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button
          hierarchy='secondary'
          color='neutral'
          size='large'
          label={<FormattedMessage id='Common.r250401.Cancel' />}
          onClick={onClose}
        />
        <Modal.Button
          size='large'
          label={<FormattedMessage id='Common.r251124.Exchange' />}
          disabled={disabled}
          onClick={() =>
            exchangeMutation.mutate({
              from: { id: exchangeCost.from, tokens: fromAmount ?? 0 },
              to: { id: exchangeCost.to, tokens: toAmount ?? 0 },
            })
          }
          isLoading={exchangeMutation.status === 'pending'}
          withSpinner
        />
      </Modal.Footer>
    </Modal.Root>
  );
};

const inputStyle = center({
  textStyle: 'Text/L/Monospace/Strong',
  color: 'content.default.level-5',
  textAlign: 'center',
  flex: 1,
  p: 16,
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.default.level-3',
  rounded: 4,
  _placeholder: {
    textStyle: 'Text/L/Regular',
    color: 'content.default.level-1',
  },
  _disabled: {
    borderColor: 'border.interaction.disabled',
    color: 'content.default.level-2',
  },
});

const buttonStyle = center({
  textStyle: 'Text/S/Strong',
  color: 'content.default.level-2',
  flex: 1,
  p: 10,
  rounded: 4,
  bgColor: 'background.default.level-3',
});
